// ** React Imports
import {
	useState,
	useRef,
	useContext,
	useEffect,
	useMemo,
	memo,
	useCallback,
} from "react";

// ** Redux & Store & Actions
import { useSelector, useDispatch } from "react-redux";
import { setMainMediaInfo, setModalEditedContent } from "store/modules/modal";

// ** Third Party Components
import { toast } from "helpers/toastWrapper";
import cloneDeep from "lodash.clonedeep";

// ** Custom Components
import Block from "components/layout/Block";

// ** Media Components
import InteractionsInMediaPostProfile from "components/user/Interactions/InteractionsInMediaPostProfile";
import MediaCommentTextEditor from "./MediaCommentTextEditor";
import LoadMoreInteractiveCommentsBtn from "./LoadMoreInteractiveCommentsBtn";
import InteractiveComment from "components/user/Interactions/InteractiveComment/InteractiveComment";

// ** Custom Hooks
import useCanComment from "hooks/useCanComment";

// ** Utils & Helpers
import api from "helpers/ApiClient";

// ** Contexts
import authContext from "contexts/authContext";
import deviceTypeContext from "contexts/deviceTypeContext";
import { openAuthModal } from "store/modules/global-common.slice";

// #####################################################

// #####################################################

const MediaPostProfileInteractions = memo(
	({
		itemId,
		itemLimit,
		itemCanLike,
		itemILiked,
		itemLikesCount,
		itemIComment,
		itemCanComment,
		itemCommentsCount,
		itemCommentsArray,
		itemMediaType,
		itemFilterComment,
		userId,
		setUserLimit,
		setIsRemoved,
		inView,
	}) => {
		const dispatch = useDispatch();
		const isAuth = useContext(authContext);
		const { isPhone } = useContext(deviceTypeContext);

		const mainMediaInfoContentId = useSelector(
			(state) => state?.modal?.mainMediaInfo?.contentId
		);
		const mainMediaInfoLikesCount = useSelector(
			(state) => state?.modal?.mainMediaInfo?.likesCount
		);
		const currentLoggedUser = useSelector((state) => state.globalUser);
		const userLimit = useSelector(
			(state) => state.userProfileMain.tolerances.limit
		);
		const modalHash = useSelector(
			(state) => state?.modal?.modalHash || null
		);
		const modalEditedContent = useSelector(
			(state) => state?.modal?.modalEditedContent
		);
		const { commentsDisabled, linkOnButtonToPayments, cantCommentMessage } =
			useCanComment({
				can_comment: itemCanComment,
				canComment: itemCanComment,
				filter_comment: itemFilterComment,
				filterComment: itemFilterComment,
				limit: typeof userLimit !== "undefined" ? userLimit : itemLimit,
			});

		const editor = useRef(null);

		const [iCommented, setICommented] = useState(itemIComment);
		const [commentsCount, setCommentsCount] = useState(itemCommentsCount);
		const [iLiked, setILiked] = useState(itemILiked);
		const [likesCount, setLikesCount] = useState(itemLikesCount);
		const [loadingComments, setLoadingComments] = useState(false);
		const [addingCommentInProgress, setAddingCommentInProgress] =
			useState(false);
		const [hash, setHash] = useState(null);
		const [commentsList, setCommentsList] = useState({
			data: cloneDeep(itemCommentsArray || []),
			total: itemCommentsCount || 0,
			refreshCount: 0,
		});
		const [visibleEditor, setVisibleEditor] = useState({
			type: null,
			id: null,
		});

		const isMe = userId === currentLoggedUser.id;
		const type = itemMediaType !== undefined ? itemMediaType : "post";

		const fetchComments = ({ page, limit, lastId }) => {
			setLoadingComments(true);
			api.get(isAuth ? `/comment/file` : `/comment/file/unauthorized`, {
				params: {
					user: userId,
					id: itemId,
					lastId,
					type,
					limit,
					page,
					nonEmpty: true,
				},
			})
				.then(({ data } = {}) => {
					setLoadingComments(false);
					if (data) {
						setICommented(data?.main_i_commented || false);
						setCommentsCount(data?.total || 0);
						setILiked(data?.main_i_liked || false);
						setCommentsList((prev) => ({
							data: lastId
								? [...prev.data, ...(data?.comments || [])]
								: data?.comments || [],
							total: data?.total || 0,
							refreshCount: prev.refreshCount + 1,
						}));

						if (itemId === mainMediaInfoContentId) {
							setLikesCount(mainMediaInfoLikesCount);
							dispatch(
								setMainMediaInfo({
									likesCount: null,
									contentId: null,
								})
							);
						}
					} else {
						setICommented(false);
						setCommentsCount(0);
						setCommentsList((prev) => ({
							data: [],
							total: 0,
							refreshCount: prev.refreshCount + 1,
						}));

						if (itemId === mainMediaInfoContentId) {
							setLikesCount(mainMediaInfoLikesCount);
							dispatch(
								setMainMediaInfo({
									likesCount: null,
									contentId: null,
								})
							);
						}
					}
				})
				.catch((error) => {
					setLoadingComments(false);
					if (error?.response?.data?.error) {
						toast.error(`${error.response.data.error}`);
					}
				});
		};

		const onCommentClick = useCallback(() => {
			if (isAuth) {
				if (
					visibleEditor.type === "ADD_COMMENT" &&
					visibleEditor.id === itemId
				) {
					setVisibleEditor({
						type: null,
						id: null,
					});
				} else {
					setVisibleEditor({
						type: "ADD_COMMENT",
						id: itemId,
					});
				}
			} else {
				dispatch(openAuthModal());
			}
		}, [isAuth, visibleEditor, itemId]);

		const addComment = useCallback(
			(text = "") => {
				setAddingCommentInProgress(true);
				api.post(
					"/comment",
					{
						user: userId,
						file: itemId,
						type,
						comment: text,
					},
					{ noTimeout: true }
				)
					.then(({ data } = {}) => {
						if (!data) {
							return;
						}

						dispatch(setUserLimit(data?.user_limit));

						setICommented(true);
						setCommentsCount(data.total);
						setCommentsList((prev) => ({
							data: [data.comments[0], ...prev.data],
							total: data.total,
							refreshCount: prev.refreshCount + 1,
						}));
						setAddingCommentInProgress(false);
						editor.current.clear();
						setVisibleEditor({
							type: null,
							id: null,
						});
					})
					.catch((error) => {
						setAddingCommentInProgress(false);
						if (error?.response?.data?.error) {
							toast.error(`${error.response.data.error}`);
						}
					});
			},
			[
				userId,
				itemId,
				type,
				dispatch,
				setICommented,
				setCommentsCount,
				setCommentsList,
				setAddingCommentInProgress,
				editor,
				setVisibleEditor,
			]
		);
		const deleteComment = useCallback(
			(data, commentId) => {
				if (data?.comments.length > 0) {
					setICommented(data?.main_i_commented);
					setCommentsCount(data?.total);
					const newComments = commentsList.data.filter(
						(comment) => comment.id !== commentId
					);

					setCommentsList((prev) => ({
						data: newComments,
						total: data.total,
						refreshCount: prev.refreshCount + 1,
					}));
				} else {
					setICommented(false);
					setCommentsCount(0);
					setCommentsList((prev) => ({
						data: [],
						total: 0,
						refreshCount: prev.refreshCount + 1,
					}));
				}
			},
			[commentsList.data]
		);

		useEffect(() => {
			if (modalHash && !hash && modalHash.includes(itemId)) {
				setHash(modalHash);
			} else if (
				!modalHash &&
				hash?.includes(itemId) &&
				modalEditedContent?.includes(itemId)
			) {
				fetchComments({
					page: 1,
					limit:
						commentsList.data.length > 3
							? commentsList.data.length
							: 3,
				});
				setHash(null);
				setModalEditedContent([]);
			} else if (!modalHash && hash && modalEditedContent?.length > 0) {
				setModalEditedContent([]);
			}
		}, [modalHash, modalEditedContent]);

		// #####################################################

		const memoizedMedia = useMemo(
			() => ({
				id: itemId,
				media_type: itemMediaType,
			}),
			[itemId, itemMediaType]
		);

		const handleSetUserLimit = useCallback(
			(value) => {
				dispatch(setUserLimit(value));
			},
			[dispatch]
		);

		const memoizedCommentsList = useMemo(() => {
			return commentsList?.data?.map((comment) => {
				return (
					<InteractiveComment
						key={`${comment.id}-${commentsList.refreshCount}`}
						comment={comment}
						media={memoizedMedia}
						profileUserId={userId}
						currentLoggedUser={currentLoggedUser}
						visibleEditor={visibleEditor}
						setVisibleEditor={setVisibleEditor}
						canLike={isAuth ? itemCanLike : true}
						commentsDisabled={commentsDisabled}
						linkOnButtonToPayments={linkOnButtonToPayments}
						cantCommentMessage={cantCommentMessage}
						setUserLimit={handleSetUserLimit}
						deleteComment={deleteComment}
						sizeUserAvatar="xsmall"
						setCommentsList={setCommentsList}
					/>
				);
			});
		}, [
			commentsList.refreshCount,
			commentsList?.data?.length,
			memoizedMedia,
			userId,
			currentLoggedUser.id,
			visibleEditor,
			setVisibleEditor,
			isAuth,
			itemCanLike,
			commentsDisabled,
			linkOnButtonToPayments,
			cantCommentMessage,
			handleSetUserLimit,
			deleteComment,
			setCommentsList,
		]);

		const memoizedMediaCommentTextEditor = useMemo(() => {
			return (
				<MediaCommentTextEditor
					isAuth={isAuth}
					isEditorVisible={
						visibleEditor.type === "ADD_COMMENT" &&
						visibleEditor.id === itemId
					}
					currentLoggedUser={currentLoggedUser}
					editor={editor}
					addComment={addComment}
					addingCommentInProgress={addingCommentInProgress}
					commentsDisabled={
						commentsDisabled || linkOnButtonToPayments
					}
					cantCommentMessage={cantCommentMessage}
					linkOnButtonToPayments={linkOnButtonToPayments}
				/>
			);
		}, [
			isAuth,
			visibleEditor,
			itemId,
			currentLoggedUser.id,
			editor,
			addComment,
			addingCommentInProgress,
			commentsDisabled,
			cantCommentMessage,
			linkOnButtonToPayments,
		]);

		const memoizedInteractionsInMediaPostProfile = useMemo(() => {
			return (
				<InteractionsInMediaPostProfile
					type={type}
					userId={userId}
					mediaId={itemId}
					canLike={isAuth ? itemCanLike : true}
					isMe={isMe}
					onCommentClick={onCommentClick}
					setIsRemoved={setIsRemoved}
					iLiked={iLiked}
					setILiked={setILiked}
					likesCount={likesCount}
					setLikesCount={setLikesCount}
					iCommented={iCommented}
					commentsCount={commentsCount}
					isActiveComment={
						visibleEditor.type === "ADD_COMMENT" &&
						visibleEditor.id === itemId
					}
				/>
			);
		}, [
			type,
			userId,
			itemId,
			isAuth,
			itemCanLike,
			isMe,
			onCommentClick,
			setIsRemoved,
			iLiked,
			setILiked,
			likesCount,
			setLikesCount,
			iCommented,
			commentsCount,
			visibleEditor,
		]);

		return (
			inView && (
				<Block px={isPhone ? 4 : 0} mt={!isPhone ? 3 : 0}>
					{memoizedInteractionsInMediaPostProfile}
					{(commentsCount ||
						visibleEditor.type === "ADD_COMMENT") && (
						<Block
							mt={
								commentsCount ||
								visibleEditor.type === "ADD_COMMENT"
									? 3
									: 0
							}
						>
							{memoizedMediaCommentTextEditor}
							{memoizedCommentsList}
							<LoadMoreInteractiveCommentsBtn
								isPhone={isPhone}
								isAuth={isAuth}
								commentsList={commentsList}
								fetchComments={fetchComments}
								loadingComments={loadingComments}
							/>
						</Block>
					)}
				</Block>
			)
		);
	}
);

// #####################################################

export default MediaPostProfileInteractions;

// #####################################################
